
import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X } from "lucide-react";
import * as XLSX from 'xlsx';
import { Resource } from "../hooks/useResourceManagement";

interface ImportResourcesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string | null;
  projectName?: string | null;
  onImportSuccess?: () => void;
}

const ImportResourcesDialog = ({ 
  open, 
  onOpenChange, 
  projectId, 
  projectName,
  onImportSuccess 
}: ImportResourcesDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileData, setFileData] = useState<any[]>([]);

  const processExcelFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('📊 Raw Excel data:', jsonData);
        setFileData(jsonData);
        setFileName(file.name);
        toast.success(`File loaded: ${jsonData.length} rows found`);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        toast.error('Error reading Excel file. Please check the format.');
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        processExcelFile(acceptedFiles[0]);
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleImport = async () => {
    if (!fileData.length || !projectId) {
      toast.error('No data to import or project not selected');
      return;
    }

    setIsProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // First, delete existing resources for this project and user
      await supabase
        .from('resources')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', user.id);

      // Process and clean the data with all required fields including project_name
      const resourcesWithMetadata = fileData.map((row: any, index: number) => ({
        name: row.Name || row.name || '',
        role: row.Role || row.role || '',
        role_type: row['Role Type'] || row.role_type || row['Role_Type'] || 'team-member',
        email: row.Email || row.email || '',
        capacity: row.Capacity || row.capacity || '8',
        rate: parseFloat(row.Rate || row.rate || '80'),
        skillset: row.Skillset || row.skillset || row['Skill Set'] || '',
        availability: row.Availability || row.availability || 'Full-time',
        years_experience: parseInt(row['Years Experience'] || row.years_experience || row['Years of Experience'] || '0', 10),
        past_projects: row['Past Projects'] || row.past_projects || row['Past_Projects'] || '',
        resource_id: index + 1,
        user_id: user.id,
        project_id: projectId,
        project_name: projectName || ''
      }));

      console.log('📝 Resources to import with all fields including project_name:', resourcesWithMetadata);

      const { error } = await supabase
        .from('resources')
        .insert(resourcesWithMetadata);

      if (error) throw error;

      toast.success(`Successfully imported ${resourcesWithMetadata.length} resources`);
      onImportSuccess?.();
      onOpenChange(false);
      setFileData([]);
      setFileName('');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import resources');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white text-black border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-black">Import Resources from Excel</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p><strong>Expected Excel columns:</strong></p>
            <p>Name, Role, Role Type, Email, Capacity (hours), Rate ($/hour), Skillset, Availability, Years Experience, Past Projects</p>
          </div>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            {isDragActive ? (
              <p>Drop the Excel file here...</p>
            ) : (
              <div>
                <p className="text-lg font-medium">Drag & drop an Excel file here</p>
                <p className="text-gray-500">or click to select a file</p>
              </div>
            )}
          </div>

          {fileName && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <FileText className="h-4 w-4" />
              <span className="flex-1">{fileName}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFileName('');
                  setFileData([]);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-300 bg-gray-100 text-black hover:bg-gray-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={fileData.length === 0 || isProcessing}
            className="bg-primary text-black hover:bg-primary/80"
          >
            {isProcessing ? 'Importing...' : `Import ${fileData.length} Resources`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportResourcesDialog;
